import type { CSSProperties } from 'react';
import './Clouds.css';
import { ATMOSPHERE_CLOUD_SVG_URL } from '../atmosphereAssets';

type CloudConfig = {
  x: number;
  y: number;
  scale: number;
  duration?: string;
};

/** Positions from welcome_screen_1.svg → Group 1321317366. */
const WELCOME_CLOUDS: readonly CloudConfig[] = [
  { x: 691, y: 64.5, scale: 1, duration: '90s' },
  { x: -102, y: 1240.85, scale: 1, duration: '105s' },
  { x: 730, y: 953.5, scale: 1, duration: '80s' },
  { x: -161, y: 339.5, scale: 1, duration: '98s' },
];

/** Positions & scales from Figma concept (118:2); base size 509×353. */
const SMALL_SCALE = 222.734375 / 509;

const DELIVERY_CLOUDS: readonly CloudConfig[] = [
  { x: 691, y: 64.5, scale: 1, duration: '90s' },
  { x: -102, y: 1626.5, scale: 1, duration: '105s' },
  { x: 730, y: 953.5, scale: 1, duration: '80s' },
  { x: -161, y: 339.5, scale: 1, duration: '98s' },
  { x: 825.626953125, y: 1651.29296875, scale: SMALL_SCALE, duration: '72s' },
  { x: 594.408203125, y: 376.60546875, scale: SMALL_SCALE, duration: '85s' },
];

export type CloudsVariant = 'welcome' | 'delivery';

export type CloudsProps = {
  variant?: CloudsVariant;
  /** Override animation; defaults: welcome=static, delivery=drift. */
  animation?: 'static' | 'drift';
};

const CLOUDS_BY_VARIANT: Record<CloudsVariant, readonly CloudConfig[]> = {
  welcome: WELCOME_CLOUDS,
  delivery: DELIVERY_CLOUDS,
};

/** Shared cloud layer with position config and optional drift animation. */
export function Clouds({
  variant = 'welcome',
  animation = variant === 'delivery' ? 'drift' : 'static',
}: CloudsProps) {
  const clouds = CLOUDS_BY_VARIANT[variant];
  const drifting = animation === 'drift';

  return (
    <div
      className={['clouds', drifting ? 'clouds--drift' : null]
        .filter(Boolean)
        .join(' ')}
      aria-hidden
    >
      {clouds.map((cloud, index) => (
        <img
          key={index}
          className="clouds__item"
          src={ATMOSPHERE_CLOUD_SVG_URL}
          alt=""
          draggable={false}
          style={
            drifting
              ? ({
                  left: cloud.x,
                  top: cloud.y,
                  '--cloud-x': `${cloud.x}px`,
                  '--cloud-scale': cloud.scale,
                  '--cloud-duration': cloud.duration ?? '90s',
                } as CSSProperties)
              : {
                  left: cloud.x,
                  top: cloud.y,
                  transform: `scale(${cloud.scale})`,
                }
          }
        />
      ))}
    </div>
  );
}
